<script setup lang="ts">
import { computed, ref } from 'vue';

interface RequestInfo {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: unknown; // object (serialized as JSON) or string / FormData description
}

interface ResponseInfo {
  status?: number;
  statusText?: string;
  headers?: Record<string, string>;
  body?: unknown; // object, string, or metadata (for binary responses)
  durationMs?: number;
}

const props = defineProps<{
  request: RequestInfo | null;
  response: ResponseInfo | null;
}>();

const expanded = ref(false);
const tab = ref<'request' | 'response'>('response');
const copied = ref<string | null>(null);

function copy(payload: string, key: string) {
  navigator.clipboard.writeText(payload);
  copied.value = key;
  setTimeout(() => (copied.value = null), 1200);
}

const curl = computed(() => {
  const r = props.request;
  if (!r) return '';
  const parts: string[] = [`curl -X ${r.method} '${r.url}'`];
  for (const [k, v] of Object.entries(r.headers ?? {})) {
    if (k.toLowerCase() === 'authorization') {
      parts.push(`  -H '${k}: Bearer <YOUR_TOKEN>'`);
    } else {
      parts.push(`  -H '${k}: ${v}'`);
    }
  }
  if (r.body !== undefined && r.body !== null) {
    const body =
      typeof r.body === 'string' ? r.body : JSON.stringify(r.body, null, 2);
    parts.push(`  -d '${body.replace(/'/g, "'\\''")}'`);
  }
  return parts.join(' \\\n');
});

const requestJSON = computed(() => {
  if (!props.request) return '';
  return JSON.stringify(props.request, null, 2);
});

const responseJSON = computed(() => {
  if (!props.response) return '';
  return JSON.stringify(props.response, null, 2);
});

// Post-response summary row pulled from headers the gateway sets.
const ledger = computed(() => {
  const h = props.response?.headers ?? {};
  const lookup = (k: string) =>
    h[k] ??
    h[k.toLowerCase()] ??
    h[k.toUpperCase()] ??
    null;
  return {
    credits: lookup('X-Asab-Credits'),
    latencyMs: props.response?.durationMs ?? lookup('X-Asab-Latency-Ms'),
    backend: lookup('X-Asab-Backend'),
    status: props.response?.status,
  };
});
</script>

<template>
  <div v-if="request || response" class="api-inspector mt-4 border rounded bg-white dark:bg-gray-800 dark:border-gray-700">
    <button
      class="w-full px-3 py-2 text-left text-xs font-medium flex items-center justify-between"
      @click="expanded = !expanded"
    >
      <span class="flex items-center gap-3">
        <span>{{ expanded ? '▾' : '▸' }} API inspector</span>
        <span v-if="ledger.status" class="text-gray-500">
          {{ ledger.status }}
          <span v-if="ledger.latencyMs">· {{ ledger.latencyMs }}ms</span>
          <span v-if="ledger.credits">· {{ ledger.credits }} credits</span>
          <span v-if="ledger.backend">· {{ ledger.backend }}</span>
        </span>
      </span>
    </button>

    <div v-if="expanded" class="border-t dark:border-gray-700">
      <div class="flex gap-1 px-2 pt-2 text-xs">
        <button
          :class="tab === 'request' ? 'bg-gray-200 dark:bg-gray-700' : ''"
          class="px-2 py-1 rounded"
          @click="tab = 'request'"
        >
          Request
        </button>
        <button
          :class="tab === 'response' ? 'bg-gray-200 dark:bg-gray-700' : ''"
          class="px-2 py-1 rounded"
          @click="tab = 'response'"
        >
          Response
        </button>
        <div class="ml-auto flex gap-1">
          <button
            v-if="request"
            class="px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200"
            @click="copy(curl, 'curl')"
          >
            {{ copied === 'curl' ? 'Copied' : 'Copy as cURL' }}
          </button>
          <button
            v-if="tab === 'request' && request"
            class="px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200"
            @click="copy(requestJSON, 'req')"
          >
            {{ copied === 'req' ? 'Copied' : 'Copy JSON' }}
          </button>
          <button
            v-if="tab === 'response' && response"
            class="px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200"
            @click="copy(responseJSON, 'res')"
          >
            {{ copied === 'res' ? 'Copied' : 'Copy JSON' }}
          </button>
        </div>
      </div>
      <pre
        class="m-2 p-2 text-xs bg-gray-50 dark:bg-gray-900 rounded overflow-auto max-h-96 whitespace-pre-wrap break-all"
        >{{ tab === 'request' ? requestJSON : responseJSON }}</pre>
    </div>
  </div>
</template>

<style scoped>
.api-inspector pre {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}
</style>
